import { Quest } from './types.d'

function escapeHTML(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

function normalizeColor(color: string | null): string | null {
  if (!color || !/^#?[0-9a-f]{6}$/i.test(color)) return null

  return color.startsWith('#') ? color : `#${color}`
}

// Знак Оригинала Клаустрофобии — та же разметка, что в quests/_original_mark.html.erb:
// обёртка во всю ширину шапки (подсказка не выходит за карточку), кнопка-ключ и
// подсказка; наведение — CSS, нажатие на телефоне — _original-mark.ts.
export function createOriginalMarkHTML(quest: Quest): string {
  if (!quest.original) return ''

  const tipId = `original-tip-${escapeHTML(quest.id)}`
  return `<div class="quest-card__original" data-original-mark><button type="button" class="original-mark" aria-label="Оригинальный квест Клаустрофобии" aria-describedby="${tipId}" data-original-mark-toggle><svg aria-hidden="true" focusable="false"><use href="#original-key"></use></svg></button><span class="quest-card__original-tip" role="tooltip" id="${tipId}">Оригинальный квест Клаустрофобии</span></div>`
}

export function createQuestCardElement(
  quest: Quest,
  mods?: Array<string>
): HTMLElement {
  const template = document.createElement('template')
  template.innerHTML = createQuestCardTemplate(quest, mods).trim()
  return template.content.firstElementChild as HTMLElement
}

export function createQuestCardTemplate(
  quest: Quest,
  mods?: Array<string>
): string {
  const metroColor = normalizeColor(quest.metro_color)
  const metroIcon = metroColor
    ? `<span class="quest-card__metro-icon" style="color: ${metroColor}"><svg><use href="#coolicon"></use></svg></span>`
    : ''
  const placeHTML = createPlaceHTML(quest, metroIcon)
  const playersHTML = quest.gamers_text
    ? `<li class="quest-card__item quest-card__item--gamers-text">${escapeHTML(quest.gamers_text)}</li>`
    : ''
  const lengthHTML =
    quest.length > 0
      ? `<li class="quest-card__item quest-card__item--length">${quest.length}&nbsp;мин.</li>`
      : ''
  // Как в quests/_quest.html.erb: только «10+», без полного текста ограничения.
  const ageHTML = quest.age_label
    ? `<li class="quest-card__item quest-card__item--age">${escapeHTML(quest.age_label)}</li>`
    : ''
  const optionsHTML = [placeHTML, playersHTML, lengthHTML, ageHTML].join('')

  const hasFearLevel = quest.fear_level > 0
  const hasComplexityLevel = !hasFearLevel && quest.complexity > 0
  const level = hasFearLevel ? quest.fear_level : quest.complexity
  const levelIcon = hasFearLevel ? 'ghost' : 'key'

  const levelHTML =
    hasFearLevel || hasComplexityLevel
      ? Array.from({ length: 5 })
          .map((_, index) => {
            return `
          <span class="${index < level ? '' : 'gray-icon'}">
            <svg>
              <use href="/sprite/sprite.svg#${levelIcon}"></use>
            </svg>
          </span>
        `
          })
          .join('')
      : ''

  // Рейтинг есть не у каждого квеста: пустой бейдж выглядел бы как оценка «ничего».
  const ratingHTML =
    quest.show_rating && quest.rating
      ? `<div class="quest-card__rating">${escapeHTML(quest.rating)}</div>`
      : ''
  // Шильдик промоакции, как в quests/_quest.html.erb: сервер отдал текст первой
  // активной кампании; без текста шильдика блок не выводится.
  const promoLabelHTML = quest.promo_label
    ? `<span class="quest-card__promo-label">${escapeHTML(quest.promo_label)}</span>`
    : ''
  const imageHTML = quest.cover_url
    ? `<div class="quest-card__image">${createCoverPicture(quest)}</div>`
    : ''
  const questPath = escapeHTML(quest.path)
  const bookingPath = quest.ticket_system
    ? questPath
    : `${questPath}#timetableSection`
  const questName = escapeHTML(quest.name)
  const cardMods = [...(mods ?? []), ...(quest.original ? ['original'] : [])]

  return `
    <article class="quest-card${cardMods.length ? ' ' + cardMods.map((mod) => 'quest-card_' + mod).join(' ') : ''}" data-quest-id="${quest.id}">
      <div class="quest-card__header">
        ${imageHTML}
        ${ratingHTML}
        ${promoLabelHTML}
        ${createTagsHTML(quest)}
      </div>
      ${createOriginalMarkHTML(quest)}
      <div class="quest-card__body">
        <p class="quest-card__congruence visually-hidden">10% попадания</p>
        <h2 class="quest-card__title">
          <a href="${questPath}">${questName}</a>
        </h2>
        <ul class="quest-card__options">
            ${optionsHTML}
        </ul>
        <div class="quest-card__controls">
          ${levelHTML ? `<div class="quest-card__level${hasFearLevel ? '' : ' quest-card__level_size_small'}">${levelHTML}</div>` : ''}
          <a class="quest-card__button button button_view_action" href="${bookingPath}">Забронировать</a>
        </div>
      </div>
    </article>
  `
}

// Шильдики, как в quests/_tags.html.erb на карточке: без ссылок, весь блок —
// ссылка на квест. Сервер уже отобрал активные и не больше двух.
function createTagsHTML(quest: Quest): string {
  if (!quest.tags?.length) return ''

  const items = quest.tags
    .map((tag) => {
      const color = normalizeColor(tag.color)
      const style = color ? ` style="background-color: ${color}"` : ''
      return `<li><span class="quest-tag"${style}>${escapeHTML(tag.title)}</span></li>`
    })
    .join('')
  return `<ul class="quest-card__tags">${items}</ul>`
}

// Как в quests/_quest.html.erb: без метро — адрес (в Краснодаре и Минске метро нет).
function createPlaceHTML(quest: Quest, metroIcon: string): string {
  if (quest.metro) {
    return `<li class="quest-card__item quest-card__item--metro">${metroIcon}м.&nbsp;${escapeHTML(quest.metro)}</li>`
  }
  if (quest.address) {
    return `<li class="quest-card__item quest-card__item--address">${escapeHTML(quest.address)}</li>`
  }
  return ''
}

// WebP-источник только когда сервер уже сделал версию (cover_webp_url), иначе
// браузер показал бы битую картинку вместо jpg.
function createCoverPicture(quest: Quest): string {
  const webpSource = quest.cover_webp_url
    ? `<source type="image/webp" srcset="${escapeHTML(quest.cover_webp_url)}">`
    : ''
  return `<picture>${webpSource}<img alt="" src="${escapeHTML(quest.cover_url ?? '')}" loading="lazy"></picture>`
}

export function createMapQuest(quest: Quest): HTMLElement {
  const template = document.createElement('template')
  template.innerHTML = createMapQuestTemplate(quest).trim()
  return template.content.firstElementChild as HTMLElement
}

export function createMapQuestTemplate(quest: Quest): string {
  const metro = quest.metro ? `м. ${escapeHTML(quest.metro)}` : ''
  const players = quest.gamers_text ? escapeHTML(quest.gamers_text) : ''
  const length = quest.length > 0 ? `${quest.length} минут` : ''
  const age = quest.age_label ? escapeHTML(quest.age_label) : ''
  const optionsHTML = [metro, players, length, age]
    .filter((opt) => opt !== '')
    .join(' · ')

  // Рейтинг есть не у каждого квеста: пустой бейдж выглядел бы как оценка «ничего».
  const ratingHTML =
    quest.show_rating && quest.rating
      ? `<div class="quest-card__rating">${escapeHTML(quest.rating)}</div>`
      : ''
  const imageHTML = quest.cover_url
    ? `<div class="quest-card__image">${createCoverPicture(quest)}</div>`
    : ''
  const questPath = escapeHTML(quest.path)
  const questName = escapeHTML(quest.name)

  return `
    <article class="quest-card quest-card_map${quest.original ? ' quest-card_original' : ''}">
      <div class="quest-card__header">
        ${imageHTML}
      </div>
      ${createOriginalMarkHTML(quest)}
      <div class="quest-card__body">
        <h2 class="quest-card__title">
          <a href="${questPath}">${questName}</a>
        </h2>
        <p class="quest-card__options">
          ${optionsHTML}
        </p>
      </div>
      ${ratingHTML}
    </article>
  `
}
